import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

type Layout = 'default' | 'col' | 'row' | 'grid';

@Component({
  selector: 'app-card-wrap',
  standalone: true,
  template: `<ng-content></ng-content>`,
  host: {
    '[class]': 'classes()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardWrapComponent {

  /**
   * Tässä listataan kaikki sallitut col-span-luokat,
   * joita tämä komponentti tukee.
   * Näin Tailwind löytää ne käännösvaiheessa.
   */
  private colSpanClasses: Record<number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12'
  };

  private layouts: Record<string, Array<string>> = {
    'default': ['flex','flex-col', 'space-y-1', 'justify-around'],
    'col': ['flex','flex-col', 'space-y-1'],
    'row': ['flex','flex-row', 'justify-between'],
    'grid': ['grid', 'gap-2']

  }

  /**
   * Sama idea taustaväreille. Voit laajentaa listaa tarpeen mukaan.
   * Käytä niitä värejä, joita oikeasti tarvitset.
   */
  private readonly bgColorClasses: Record<string, string> = {
    white: 'bg-white',
    gray: 'bg-gray-100',
    // jne...
  };

  /**
   * Näiden avulla voit valita dynaamisesti, mitä col-span-,
   * bgColor- ja border-luokkaa käytetään.
   */
  public colSpan = input<number>(1);
  public bgColor = input<string>('white');
  public border = input<boolean>(true);
  public layout = input<Layout>('default');

  /**
   * Staattiset luokat, joita käytetään joka tapauksessa.
   */
  private readonly staticClasses: string[] = [
    'px-4',
    'py-2',
    'box-content',
  ];

  /**
   * Lasketaan kaikki luokat yhteen. Koska Tailwind ei tue
   * täysin dynaamisia class-stringejä (kuten `bg-${value}` suoraan),
   * jokainen variantti tulee olla jossakin muodossa valmiina.
   */
  public classes = computed(() => {
    // Valitaan col-span-luokka
    const spanClass: string = this.colSpanClasses[this.colSpan()] ?? 'col-span-1';

    // Valitaan taustaväri
    const bgClass: string = this.bgColorClasses[this.bgColor()] ?? 'bg-white';

    // Jos border on true, lisätään border-luokat
    const borderClasses: string[] = this.border()
        ? ['border', 'border-gray-300']
        : [];

    const layoutClasses: string[] = this.layouts[this.layout()]

    // Kootaan kaikki luokat yhteen
    return [
      ...this.staticClasses,
      spanClass,
      bgClass,
      ...borderClasses,
      ...layoutClasses
    ].join(' ');
  });
}
